// Classical Bhava Bala (House Strength) calculation per BPHS
import type { PlanetName } from './astroEngine';
import { SIGN_LORDS, getHouseSign } from './lordshipUtils';

export type BhavaBalaRow = {
  house: number;
  bhavaAdhipatiBala: number;  // House lord strength in virupas
  bhavaDigBala: number;        // Directional strength in virupas
  bhavaDrishtiBala: number;    // Aspect strength in virupas (can be negative)
  totalBala: number;           // Sum of all components in virupas
  rupas: number;               // Total / 60 (standard unit)
  percentage: number;          // Rupas scaled to 0-100% (rupas * 10, since max ~10 rupas)
};

// Directional strength by house (Dig Bala)
// Kendra houses (1,4,7,10) get maximum strength for specific planets
const DIG_BALA_BY_HOUSE: Record<number, Partial<Record<PlanetName, number>>> = {
  1: { Jupiter: 60, Mercury: 60 },  // Ascendant: Jupiter & Mercury full strength
  4: { Moon: 60, Venus: 60 },       // IC: Moon & Venus full strength
  7: { Saturn: 60 },                // Descendant: Saturn full strength
  10: { Sun: 60, Mars: 60 },        // MC: Sun & Mars full strength
};

/**
 * Calculate Bhava Adhipati Bala (House Lord Strength)
 * This is the Shadbala strength of the house lord in virupas (not scaled)
 */
function calculateBhavaAdhipatiBala(
  house: number,
  ascendantSign: number,
  planetShadbalaVirupas: Partial<Record<PlanetName, number>>
): number {
  const houseSign = getHouseSign(house as any, ascendantSign);
  const lord = SIGN_LORDS[houseSign];
  
  if (!lord) return 390; // Default neutral (~390 virupas, or 6.5 rupas)
  
  const lordVirupas = planetShadbalaVirupas[lord] ?? 390; // Default ~390 virupas
  
  // Return Shadbala in virupas directly
  return lordVirupas;
}

/**
 * Calculate Bhava Dig Bala (Directional Strength of House)
 * Classical BPHS values: Kendras get varying strength, others get lower values
 */
function calculateBhavaDigBala(house: number): number {
  // Classical Bhava Dig Bala values matching Astro-Sage
  const digBalaValues: Record<number, number> = {
    1: 30,   // Ascendant (kendra)
    2: 20,   // Panapara
    3: 40,   // Apoklima
    4: 30,   // IC (kendra)
    5: 40,   // Panapara
    6: 10,   // Apoklima
    7: 30,   // Descendant (kendra)
    8: 10,   // Panapara
    9: 10,   // Apoklima
    10: 60,  // MC (kendra) - strongest
    11: 50,  // Panapara
    12: 50   // Apoklima
  };
  
  return digBalaValues[house] ?? 30;
}

/**
 * Calculate Bhava Drishti Bala (Aspect Strength on House)
 * Sum of aspects from benefics (positive) and malefics (negative) in virupas
 * Benefics add strength, malefics subtract. Can be negative.
 */
function calculateBhavaDrishtiBala(
  house: number,
  aspects: Array<{ from: PlanetName; toHouse: number }>,
  planetStrengthPct: Partial<Record<PlanetName, number>>,
  functionalMap?: Partial<Record<PlanetName, 'benefic' | 'malefic' | 'neutral'>>
): number {
  // Filter aspects to this house
  const houseAspects = aspects.filter(a => a.toHouse === house);
  
  if (houseAspects.length === 0) return 0; // No aspect = 0 (neutral)
  
  let totalDrishtiBala = 0;
  
  for (const aspect of houseAspects) {
    const planet = aspect.from;
    const strength = planetStrengthPct[planet] ?? 0; // 0-100 typically
    const functional = functionalMap?.[planet] ?? 'neutral';
    
    // Convert strength (0-100) to virupas contribution
    // Assume average strength planet contributes ±30 virupas
    let aspectValue = (strength / 100) * 30;
    
    if (functional === 'malefic') {
      aspectValue = -Math.abs(aspectValue); // Malefic aspects are negative
    } else if (functional === 'benefic') {
      aspectValue = Math.abs(aspectValue); // Benefic aspects are positive
    }
    // Neutral aspects: use actual strength proportionally
    
    totalDrishtiBala += aspectValue;
  }
  
  return totalDrishtiBala; // Can be positive or negative
}

export interface BhavaBalaInput {
  ascendantSign: number; // 1-12 (Aries=1, etc.)
  planetShadbalaVirupas: Partial<Record<PlanetName, number>>; // Shadbala in virupas (not rupas!)
  planetPlacements: Partial<Record<PlanetName, { house: number }>>; // D1 house placements
  aspects: Array<{ from: PlanetName; toHouse: number }>; // Parashara aspects
  planetStrengthPct?: Partial<Record<PlanetName, number>>; // Blended % (0-100)
  functionalMap?: Partial<Record<PlanetName, 'benefic' | 'malefic' | 'neutral'>>;
}

/**
 * Calculate Bhava Bala (House Strength) for all 12 houses
 * Returns strength in standard components + total rupas
 */
export function computeBhavaBala(input: BhavaBalaInput): BhavaBalaRow[] {
  const {
    ascendantSign,
    planetShadbalaVirupas,
    planetPlacements,
    aspects,
    planetStrengthPct = {},
    functionalMap = {}
  } = input;
  
  const results: BhavaBalaRow[] = [];
  
  for (let house = 1; house <= 12; house++) {
    const bhavaAdhipatiBala = calculateBhavaAdhipatiBala(
      house,
      ascendantSign,
      planetShadbalaVirupas
    );
    
    const bhavaDigBala = calculateBhavaDigBala(house);
    
    const bhavaDrishtiBala = calculateBhavaDrishtiBala(
      house,
      aspects,
      planetStrengthPct,
      functionalMap
    );
    
    const totalBala = bhavaAdhipatiBala + bhavaDigBala + bhavaDrishtiBala;
    const rupas = totalBala / 60; // Convert virupas to rupas (standard unit)
    
    // Debug logging for first 3 houses
    if (house <= 3) {
      const houseSign = ((ascendantSign + house - 2) % 12) + 1;
      const lord = SIGN_LORDS[houseSign];
      console.log(`[BhavaBala] H${house} (sign ${houseSign}, lord ${lord}):`, {
        adhipati: bhavaAdhipatiBala.toFixed(2),
        dig: bhavaDigBala,
        drishti: bhavaDrishtiBala.toFixed(2),
        total: totalBala.toFixed(2),
        rupas: rupas.toFixed(2),
        lordShadbalaVirupas: planetShadbalaVirupas[lord]?.toFixed(2)
      });
    }
    
    // Convert to percentage: scale rupas to 0-100 (assuming max ~10 rupas = 100%)
    const percentage = Math.round(rupas * 10 * 100) / 100; // rupas * 10 gives percentage
    
    results.push({
      house,
      bhavaAdhipatiBala: Math.round(bhavaAdhipatiBala * 100) / 100,
      bhavaDigBala: Math.round(bhavaDigBala * 100) / 100,
      bhavaDrishtiBala: Math.round(bhavaDrishtiBala * 100) / 100,
      totalBala: Math.round(totalBala * 100) / 100,
      rupas: Math.round(rupas * 100) / 100,
      percentage
    });
  }
  
  return results;
}
